import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { PickerButton } from "./PickerButton";

declare const __APP_VERSION__: string;

/**
 * Full-height left chrome — owns the brand, the venue picker (only when an
 * org is in scope), and the three nested nav sections (FPZERO / Org / Venue).
 * The organisation switcher lives in the topbar so it sits in the same row
 * as the user menu and reads as a global "what am I looking at" picker.
 */
export function Sidebar() {
  const { user } = useAuth();
  const { currentOrg, venues, currentVenue } = useOrgContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const inVenueScope = Boolean(currentVenue);
  const [orgExpanded, setOrgExpanded] = useState(false);

  // FPZERO section defaults to expanded on admin routes / before any org is
  // chosen, and to collapsed once the user is inside an org. The user can
  // override either way by clicking the chevron; navigating between admin
  // ↔ org resets the section's default.
  const [fpzeroExpanded, setFpzeroExpanded] = useState(true);
  useEffect(() => {
    setFpzeroExpanded(!currentOrg || isAdminRoute);
  }, [currentOrg?.id, isAdminRoute]);

  const showVenuePicker = !!currentOrg && !isAdminRoute;

  return (
    <nav className="w-64 shrink-0 border-r border-gray-200 bg-white flex flex-col h-screen">
      {/* Brand */}
      <Link
        to={user?.isFpzeroAdmin ? "/" : currentOrg ? `/org/${currentOrg.slug}` : "/"}
        className="flex items-baseline gap-2 px-4 py-3 border-b border-gray-200 hover:bg-gray-50"
      >
        <span className="font-bold text-gray-900">VMS Cloud</span>
        <span className="text-[10px] text-gray-400">v{__APP_VERSION__}</span>
      </Link>

      {/* Nav sections — scrollable to handle small viewports */}
      <div className="flex-1 overflow-y-auto py-2">
        {user?.isFpzeroAdmin && (
          <Section
            heading="FPZERO"
            highlight={isAdminRoute}
            collapsible={!!currentOrg}
            collapsed={!!currentOrg && !fpzeroExpanded}
            onToggle={() => setFpzeroExpanded((v) => !v)}
          >
            <SidebarLink to="/" end label="Operations Overview" />
            <SidebarLink to="/admin/organisations" label="Organisations" />
            <SidebarLink to="/admin/releases" label="Releases" />
            <SidebarLink to="/admin/licences" label="Licences" />
            <SidebarLink to="/admin/sync" label="Sync health" />
            <SidebarLink to="/admin/audit" label="Audit log" />
          </Section>
        )}

        {showVenuePicker && (
          <div className="px-3 py-3 border-b border-gray-100 bg-gray-50/40">
            <PickerButton
              label="Venue"
              value={currentVenue?.name ?? "All venues"}
              matchTriggerWidth
              items={[
                {
                  id: "__all__",
                  label: "All venues",
                  to: `/org/${currentOrg!.slug}`,
                  active: !currentVenue,
                },
                ...venues.map((v) => ({
                  id: v.id,
                  label: v.name,
                  to: `/org/${currentOrg!.slug}/venue/${v.slug}`,
                  active: currentVenue?.id === v.id,
                })),
                ...(venues.length === 0
                  ? [{ id: "__none__", label: "No venues", disabled: true }]
                  : []),
              ]}
              onPick={(item) => item.to && navigate(item.to)}
            />
          </div>
        )}

        {currentOrg && (
          <Section
            heading={currentOrg.name}
            collapsible={inVenueScope}
            collapsed={inVenueScope && !orgExpanded}
            onToggle={() => setOrgExpanded((v) => !v)}
          >
            <SidebarLink to={`/org/${currentOrg.slug}`} end label="Dashboard" />
            <SidebarLink to={`/org/${currentOrg.slug}/venues`} label="Venues" />
            <SidebarLink to={`/org/${currentOrg.slug}/drivers`} label="Drivers" />
            <SidebarLink to={`/org/${currentOrg.slug}/bookings`} label="Bookings" />
            <SidebarLink to={`/org/${currentOrg.slug}/leaderboards`} label="Leaderboards" />
            <SidebarLink to={`/org/${currentOrg.slug}/templates/branding`} label="Branding templates" />
            <SidebarLink to={`/org/${currentOrg.slug}/templates/race`} label="Race templates" />
            <SidebarLink to={`/org/${currentOrg.slug}/templates/difficulty`} label="Difficulty templates" />
            <SidebarLink to={`/org/${currentOrg.slug}/partners`} label="Partner keys" />
            <SidebarLink to={`/org/${currentOrg.slug}/webhooks`} label="Webhooks" />
            <SidebarLink to={`/org/${currentOrg.slug}/members`} label="Members" />
            <SidebarLink to={`/org/${currentOrg.slug}/settings`} label="Settings" />
          </Section>
        )}

        {currentOrg && currentVenue && (
          <Section heading={currentVenue.name} accent>
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}`} end label="Dashboard" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/events`} label="Events" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/sessions`} label="Sessions" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/lap-times`} label="Lap times" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/results`} label="Results" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/leaderboards`} label="Leaderboards" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/bookings`} label="Bookings" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/drivers`} label="Drivers (visited)" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/audit`} label="Audit" />
            <SidebarLink to={`/org/${currentOrg.slug}/venue/${currentVenue.slug}/settings`} label="Settings" />
          </Section>
        )}
      </div>
    </nav>
  );
}

function Section({
  heading,
  highlight,
  accent,
  collapsible,
  collapsed,
  onToggle,
  children,
}: {
  heading: string;
  highlight?: boolean;
  accent?: boolean;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={`border-b border-gray-100 py-3 ${accent ? "bg-blue-50/40" : ""}`}>
      <button
        type="button"
        onClick={collapsible ? onToggle : undefined}
        className={`w-full flex items-center justify-between px-4 mb-1 text-xs font-semibold uppercase tracking-wide ${
          highlight ? "text-purple-700" : "text-gray-500"
        } ${collapsible ? "hover:text-gray-800 cursor-pointer" : "cursor-default"}`}
      >
        <span className="truncate">{heading}</span>
        {collapsible && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            className={`transition-transform ${collapsed ? "" : "rotate-90"}`}
          >
            <path
              d="M4 2.5L8 6L4 9.5"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {!collapsed && <ul className="space-y-0.5">{children}</ul>}
    </div>
  );
}

function SidebarLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          `block mx-2 rounded-md px-3 py-1.5 text-sm ${
            isActive
              ? "bg-primary text-white font-medium"
              : "text-gray-700 hover:bg-gray-100"
          }`
        }
      >
        {label}
      </NavLink>
    </li>
  );
}
